import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Loader2, RefreshCw, ExternalLink } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Variant {
  id: string;
  label: string;
  props: Record<string, unknown>;
}

interface Manifest {
  name: string;
  subject: string;
  displayName: string | null;
  variants: Variant[];
}

// Params we care about surfacing on the metadata panel — keeps the eye
// focused on the fields most likely to differ between test and live.
const HIGHLIGHT_KEYS = [
  "retryUrl",
  "openPixelUrl",
  "failureReason",
  "nextRetryOn",
  "accessEndsOn",
  "productPrice",
  "billingInterval",
] as const;

const PROJECT_REF = "vqxznxajxsoskkojnhqn";
const FN_BASE = `https://${PROJECT_REF}.supabase.co/functions/v1/preview-email-template`;

// Parse ?price=/?utm_campaign=/etc so we can display them cleanly.
function summarizeUrl(raw: string | undefined | null): Array<[string, string]> {
  if (!raw) return [];
  try {
    const u = new URL(raw);
    const rows: Array<[string, string]> = [
      ["host", u.host + u.pathname],
    ];
    u.searchParams.forEach((v, k) => rows.push([k, v]));
    return rows;
  } catch {
    return [["value", raw]];
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AdminEmailPreviews() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [manifest, setManifest] = useState<Manifest[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Admin gate — the edge function also enforces this server-side.
  useEffect(() => {
    (async () => {
      const { data: session } = await supabase.auth.getSession();
      const uid = session.session?.user.id;
      setToken(session.session?.access_token ?? null);
      if (!uid) {
        setIsAdmin(false);
        return;
      }
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
    })();
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setLoadError(null);
    const { data, error } = await supabase.functions.invoke("preview-email-template", {
      method: "GET",
    });
    if (error) {
      setLoadError(error.message);
      setManifest([]);
    } else {
      setManifest((data as { templates: Manifest[] })?.templates ?? []);
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin, refresh]);

  // Build the render URL for an iframe. Each iframe fetches the HTML
  // for one (template, variant) combo. We inline the bearer token as a
  // header via a blob-URL trick isn't possible with iframes, so we
  // fetch → build a blob URL → point the iframe at it.
  const [iframeSrcs, setIframeSrcs] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!token || manifest.length === 0) return;
    let cancelled = false;
    const urls: string[] = [];
    (async () => {
      const next: Record<string, string> = {};
      for (const tpl of manifest) {
        for (const v of tpl.variants) {
          const res = await fetch(
            `${FN_BASE}?template=${encodeURIComponent(tpl.name)}&variant=${encodeURIComponent(v.id)}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (!res.ok) continue;
          const html = await res.text();
          const blob = new Blob([html], { type: "text/html" });
          const url = URL.createObjectURL(blob);
          urls.push(url);
          next[`${tpl.name}::${v.id}`] = url;
        }
      }
      if (!cancelled) setIframeSrcs(next);
    })();
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [manifest, token]);

  const totalVariants = useMemo(
    () => manifest.reduce((acc, t) => acc + t.variants.length, 0),
    [manifest],
  );

  if (isAdmin === false) {
    return (
      <main className="container py-16">
        <Helmet>
          <title>Admin — Email Previews</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <Alert variant="destructive">
          <AlertTitle>Admins only</AlertTitle>
          <AlertDescription>
            You need the <code>admin</code> role to view this page.
          </AlertDescription>
        </Alert>
      </main>
    );
  }

  return (
    <main className="container py-8 space-y-6">
      <Helmet>
        <title>Admin — Email Previews</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Transactional Email Previews</h1>
          <p className="text-sm text-muted-foreground">
            Side-by-side test vs. live variants for every registered template.
            Use this to spot-check retry URL parameters, campaign attribution,
            and branding drift before shipping template changes.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={refreshing || loading}>
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading manifest…
        </div>
      ) : loadError ? (
        <Alert variant="destructive">
          <AlertTitle>Failed to load previews</AlertTitle>
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {manifest.length} template{manifest.length === 1 ? "" : "s"} · {totalVariants} variant
            {totalVariants === 1 ? "" : "s"}
          </p>

          <div className="space-y-8">
            {manifest.map((tpl) => (
              <TemplateBlock
                key={tpl.name}
                template={tpl}
                iframeSrcs={iframeSrcs}
              />
            ))}
          </div>
        </>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TemplateBlock({
  template,
  iframeSrcs,
}: {
  template: Manifest;
  iframeSrcs: Record<string, string>;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="text-lg font-semibold">
              {template.displayName ?? template.name}
            </CardTitle>
            <p className="text-xs text-muted-foreground font-mono">{template.name}</p>
          </div>
          <div className="text-xs text-muted-foreground">
            <span className="font-medium">Subject:</span> {template.subject}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 lg:grid-cols-2">
          {template.variants.map((variant) => (
            <VariantCard
              key={variant.id}
              templateName={template.name}
              variant={variant}
              iframeSrc={iframeSrcs[`${template.name}::${variant.id}`]}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function VariantCard({
  templateName,
  variant,
  iframeSrc,
}: {
  templateName: string;
  variant: Variant;
  iframeSrc: string | undefined;
}) {
  const props = variant.props;
  const retryRows = summarizeUrl(props.retryUrl as string | undefined);
  const pixelRows = summarizeUrl(props.openPixelUrl as string | undefined);

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="uppercase tracking-wide text-[10px]">
            {variant.id}
          </Badge>
          <span className="text-sm font-medium">{variant.label}</span>
        </div>
        {iframeSrc ? (
          <a
            href={iframeSrc}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            Open <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </div>

      <div className="p-3 space-y-3">
        {iframeSrc ? (
          <iframe
            title={`${templateName} · ${variant.id}`}
            src={iframeSrc}
            className="w-full h-[520px] rounded border bg-white"
            sandbox="allow-same-origin"
          />
        ) : (
          <div className="w-full h-[520px] rounded border flex items-center justify-center text-sm text-muted-foreground bg-muted/30">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Rendering…
          </div>
        )}

        <MetadataPanel props={props} retryRows={retryRows} pixelRows={pixelRows} />
      </div>
    </div>
  );
}

function MetadataPanel({
  props,
  retryRows,
  pixelRows,
}: {
  props: Record<string, unknown>;
  retryRows: Array<[string, string]>;
  pixelRows: Array<[string, string]>;
}) {
  const highlights = HIGHLIGHT_KEYS
    .map((k) => [k, props[k]] as const)
    .filter(([, v]) => v !== undefined && v !== null && v !== "");

  return (
    <div className="space-y-3 text-xs">
      {highlights.length > 0 && (
        <section>
          <h4 className="font-semibold uppercase tracking-wide text-muted-foreground text-[10px] mb-1">
            Props
          </h4>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
            {highlights.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-muted-foreground font-mono">{k}</dt>
                <dd className="font-mono break-all">{String(v)}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {retryRows.length > 0 && (
        <ParamTable title="retryUrl params" rows={retryRows} />
      )}
      {pixelRows.length > 0 && (
        <ParamTable title="openPixelUrl params" rows={pixelRows} />
      )}
    </div>
  );
}

function ParamTable({
  title,
  rows,
}: {
  title: string;
  rows: Array<[string, string]>;
}) {
  return (
    <section>
      <h4 className="font-semibold uppercase tracking-wide text-muted-foreground text-[10px] mb-1">
        {title}
      </h4>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted-foreground font-mono">{k}</dt>
            <dd className="font-mono break-all">{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
